/** Curated complement controller: local DOM data only; product-form owns cart mutations and theme events. */
export function shippingRemaining(cart, country, currency) {
  if (!cart?.items?.some(item => item.requires_shipping)) return 0;
  const total = Math.max(0, cart.items.filter(item => item.requires_shipping).reduce((sum, item) => sum + item.final_line_price, 0) - (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0));
  if (country === 'JP' && currency === 'JPY') return Math.max(0, 500000 - total);
  if (country === 'HK' && currency === 'HKD') return Math.max(0, 40001 - total);
  return 0;
}
export function cartExclusions(items, map, country) {
  const excluded = new Set(); let hold = false;
  for (const item of items || []) {
    const record = map[String(item.product_id)];
    if (!record?.supported || (country && !record.markets.includes(country))) hold = true;
    if (record) { excluded.add(record.handle); for (const handle of record.bom) excluded.add(handle); }
    if (item.handle) excluded.add(item.handle);
  }
  return { excluded, hold };
}
export function rankCandidates(cards, remaining) {
  return [...cards].sort((a, b) => {
    // A direct first-choice complement remains ahead of secondary complements; price breaks relevance ties.
    const relevance = Number(a.rank) - Number(b.rank);
    if (relevance) return relevance;
    const distance = card => remaining > 0 ? (Number(card.price) >= remaining ? Number(card.price) - remaining : 1e12 + remaining - Number(card.price)) : Number(card.price);
    return distance(a) - distance(b);
  }).slice(0, 2);
}
export function upgradePair(pairs, sourceId) {
  if (sourceId == null) return undefined;
  return pairs.find(pair => pair.sourceId === String(sourceId));
}
if (typeof HTMLElement !== 'undefined' && !customElements.get('aras-recommendations')) {
  customElements.define('aras-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.map = JSON.parse(this.querySelector('[data-cart-map]')?.textContent || '{}');
      this.cards = [...this.querySelectorAll('[data-rec-card]')];
      this.remaining = Number(this.dataset.remaining);
      this.dataset.enhanced = '';
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-rec-variant]'); if (!select) return;
        const card = select.closest('[data-rec-card]'); const option = select.selectedOptions[0];
        card.dataset.price = option.dataset.price || card.dataset.price;
        card.querySelector('[data-rec-price]').textContent = option.dataset.money || '';
        const status = card.querySelector('[data-rec-status]'); status.textContent = ''; status.removeAttribute('data-error');
        this.rank();
      }, { signal });
      this.addEventListener('submit', event => {
        const form = event.target; if (!form.closest('[data-rec-card]')) return;
        const status = form.querySelector('[data-rec-status]');
        // The theme checks aria-busy itself. Do not preemptively set that attribute before its listener runs.
        if (form.dataset.pending === 'true') { event.preventDefault(); event.stopImmediatePropagation(); return; }
        if (!form.checkValidity()) return;
        form.dataset.pending = 'true'; status.textContent = this.dataset.loading; status.removeAttribute('data-error');
        // Theme's fetch network exception has no cart:error event. Restore a usable state after a bounded wait.
        clearTimeout(form.recTimeout); form.recTimeout = setTimeout(() => {
          status.textContent = this.dataset.networkHold; status.setAttribute('data-error', '');
          for (const button of form.querySelectorAll('[type=submit]')) { button.removeAttribute('aria-busy'); button.disabled = true; }
        }, 20000);
      }, { capture: true, signal });
      this.addEventListener('cart:error', event => {
        const form = event.target.closest('form'); if (form) this.release(form, event.detail?.error || this.dataset.error, true);
      }, { signal });
      this.addEventListener('variant:add', event => {
        const form = event.target.closest('form'); if (form) this.release(form, this.dataset.added, false);
      }, { signal });
      document.addEventListener('variant:change', event => {
        if (event.target.closest('aras-recommendations')) return;
        this.updateUpgrade(event.detail?.variant?.id);
      }, { signal });
      document.addEventListener('cart:change', event => {
        const cart = event.detail?.cart; if (!cart?.items) return;
        const { excluded, hold } = cartExclusions(cart.items, this.map, this.dataset.country);
        this.remaining = shippingRemaining(cart, this.dataset.country, this.dataset.currency);
        for (const card of this.cards) card.dataset.excluded = String(hold || excluded.has(card.dataset.handle));
        // Shipping copy was rendered for the previous snapshot. Hide it until the section is rerendered.
        this.querySelector('[data-rec-shipping]')?.setAttribute('hidden', '');
        this.rank();
      }, { signal });
      this.rank();
    }
    disconnectedCallback() { this.abort?.abort(); for (const form of this.querySelectorAll('form')) clearTimeout(form.recTimeout); }
    release(form, message, error) {
      clearTimeout(form.recTimeout); delete form.dataset.pending;
      const status = form.querySelector('[data-rec-status]'); if (status) { status.textContent = message; status.toggleAttribute('data-error', error); }
      for (const button of form.querySelectorAll('[type=submit]')) { button.removeAttribute('aria-busy'); button.disabled = false; }
    }
    rank() {
      const eligible = this.cards.filter(card => card.dataset.excluded !== 'true');
      const chosen = rankCandidates(eligible.map(card => ({ element: card, rank: card.dataset.rank, price: card.dataset.price })), this.remaining).map(card => card.element);
      for (const card of this.cards) card.hidden = !chosen.includes(card);
      const group = this.querySelector('[data-rec-complements]'); if (group) group.hidden = chosen.length === 0;
    }
    updateUpgrade(id) {
      const upgrade = this.querySelector('[data-rec-upgrade]'); if (!upgrade) return;
      const pairData = upgradePair([...upgrade.querySelectorAll('[data-upgrade-pair]')].map(item => item.dataset), id);
      const pair = pairData ? { dataset: pairData } : undefined;
      upgrade.querySelector('[data-upgrade-match]').hidden = !pair;
      upgrade.querySelector('[data-upgrade-hold]').hidden = Boolean(pair);
      if (!pair) return;
      upgrade.querySelector('[data-upgrade-color]').textContent = pair.dataset.color;
      upgrade.querySelector('[data-upgrade-total]').textContent = pair.dataset.total;
      upgrade.querySelector('[data-upgrade-difference]').textContent = pair.dataset.difference;
      const url = new URL(upgrade.dataset.setUrl, location.origin); url.searchParams.set('variant', pair.dataset.targetId);
      upgrade.querySelector('[data-upgrade-link]').href = url.href;
    }
  });
}
